import type { OrgRole, Permission } from './types';
import { ORG_ROLES, PERMISSIONS } from './types';

// 角色矩阵的调用方通常只 import './roles'，因此把两个总表一并转出去。
export { ORG_ROLES, PERMISSIONS } from './types';

/**
 * 组织 RBAC 矩阵 —— 唯一事实来源。
 *
 * 任何权限判断都必须走 `hasPermission` / `hasAnyPermission`，
 * 不要在业务代码里手写 `role === 'admin'` 之类的比较。
 */
export const ROLE_PERMISSIONS: Readonly<Record<OrgRole, readonly Permission[]>> = {
  /** 组织所有者：全部权限，含解散组织。一个组织有且仅有一个。 */
  owner: PERMISSIONS,

  /** 管理员：可管成员、邀请、接入应用与审计清理；唯独不能解散组织。 */
  admin: [
    'org.view',
    'org.manage',
    'member.view',
    'member.invite',
    'member.manage',
    'role.view',
    'app.view',
    'app.manage',
    'audit.view',
    'audit.prune',
    'settings.manage',
    // 计费：admin 只能查看订单与消费明细，下单/付款/退款/开票只留给 owner（见 docs/billing.md §3.1）。
    'billing.view',
    'support.view',
    'support.create',
  ],

  /** 成员：可看组织与成员名册，可管理自己的安全设置（后者不依赖权限位）。 */
  member: ['org.view', 'member.view', 'role.view', 'support.view', 'support.create'],

  /** 观察员：只读。 */
  viewer: ['org.view', 'member.view', 'support.view'],
};

export const ROLE_LABELS: Readonly<Record<OrgRole, string>> = {
  owner: '所有者',
  admin: '管理员',
  member: '成员',
  viewer: '观察员',
};

export const ROLE_DESCRIPTIONS: Readonly<Record<OrgRole, string>> = {
  owner: '拥有组织全部权限，可解散组织与转让所有权。',
  admin: '可管理成员、邀请、角色说明、接入应用与操作记录。',
  member: '可查看组织信息与成员名册，并管理自己的口令与两步验证。',
  viewer: '仅可查看组织信息与成员名册，不能做任何修改。',
};

export const PERMISSION_LABELS: Readonly<Record<Permission, string>> = {
  'org.view': '查看组织信息',
  'org.manage': '编辑组织资料',
  'org.delete': '解散组织',
  'member.view': '查看成员名册',
  'member.invite': '邀请成员',
  'member.manage': '管理成员与角色',
  'role.view': '查看角色与权限',
  'app.view': '查看接入应用',
  'app.manage': '管理接入应用与密钥',
  'audit.view': '查看操作记录',
  'audit.prune': '清理操作记录',
  'settings.manage': '管理组织设置',
  'billing.view': '查看订单与消费明细',
  'billing.manage': '下单、付款与退款',
  'billing.invoice': '申请与查看发票',
  'support.view': '查看工单',
  'support.create': '提交工单与回复',
};

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === 'string' && (ORG_ROLES as readonly string[]).includes(value);
}

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** 列出某角色的全部权限。 */
export function permissionsForRole(role: OrgRole): Permission[] {
  return [...(ROLE_PERMISSIONS[role] ?? [])];
}

/** 单一权限判定。未知角色一律拒绝。 */
export function hasPermission(role: OrgRole | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const granted = ROLE_PERMISSIONS[role];
  if (!granted) return false;
  return granted.includes(permission);
}

/** 任一权限命中即通过。 */
export function hasAnyPermission(
  role: OrgRole | null | undefined,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((permission) => hasPermission(role, permission));
}

/**
 * 与 platform 的角色映射。
 *
 * platform 目前有自己的 users 表与四档角色（admin / operator / maintainer / viewer）。
 * 它接入 OIDC 后按本映射把组织角色降级为自己的角色，本文件是这份契约的唯一出处。
 */
export const PLATFORM_ROLE_BY_ORG_ROLE: Readonly<Record<OrgRole, string>> = {
  owner: 'admin',
  admin: 'admin',
  member: 'operator',
  viewer: 'viewer',
};

